import React from "react";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useSelector } from "react-redux";

interface DateRangeSelectorProps {
  dateRange: string;
  fromDate: string;
  toDate: string;
  onDateRangeChange: (value: string) => void;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
  permissions?: any;
  /** Lab tabs: Today, Yesterday, Last 7 days, This Month, Last Month, This Year, Last Year, Custom */
  /** Blood bank: Today, Yesterday, This Month, Last Month, This Year, Last Year, Custom */
  preset?: "default" | "lab" | "blood";
  /** Hide from/to until Custom is selected */
  compact?: boolean;
}

const LAB_DATE_RANGE_VALUES = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "This Month",
  "Last Month",
  "This Year",
  "Last Year",
  "custom",
];

const BLOOD_DATE_RANGE_VALUES = [
  "Today",
  "Yesterday",
  "This Month",
  "Last Month",
  "This Year",
  "Last Year",
  "custom",
];

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateRange,
  fromDate,
  toDate,
  onDateRangeChange,
  onFromDateChange,
  onToDateChange,
  permissions,
  preset = "default",
  compact = false,
}) => {
  // Get user profile from Redux store
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const accountPermissions = profile?.data?.accessConfigurations?.Account || {};
  const effectivePermissions = permissions || accountPermissions;

  // Function to calculate dates based on selected range
  const calculateDates = (selectedRange: string) => {
    const today = new Date();
    const formatDate = (date: Date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    let from = "";
    let to = "";

    switch (selectedRange) {
      case "Today":
        from = to = formatDate(today);
        break;

      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        from = to = formatDate(yesterday);
        break;

      case "Last 7 days":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        from = formatDate(sevenDaysAgo);
        to = formatDate(today);
        break;

      case "This Month":
        const firstDayOfMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1
        );
        from = formatDate(firstDayOfMonth);
        to = formatDate(today);
        break;

      case "Last Month":
        // Complete previous month only (e.g., if current is May 2026, then April 1-30, 2026)
        const firstDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        from = formatDate(firstDayOfLastMonth);
        to = formatDate(lastDayOfLastMonth);
        break;

      case "Last 6 months":
        // Relative to today (e.g., if today is April 6, then Oct 6 to April 6)
        const last6MonthsAgo = new Date(today);
        last6MonthsAgo.setMonth(today.getMonth() - 6);
        from = formatDate(last6MonthsAgo);
        to = formatDate(today);
        break;

      case "This Year":
        const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
        from = formatDate(firstDayOfYear);
        to = formatDate(today);
        break;

      case "Last Year":
        // Complete previous calendar year (e.g., if current is 2026, then Jan 1 - Dec 31, 2025)
        const firstDayOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
        const lastDayOfLastYear = new Date(today.getFullYear() - 1, 11, 31);
        from = formatDate(firstDayOfLastYear);
        to = formatDate(lastDayOfLastYear);
        break;

      case "limited custom":
        // Default both to today; user can pick within 1-month window
        from = to = formatDate(today);
        break;

      default:
        // For custom or unknown ranges, don't auto-calculate
        return;
    }

    // Update the dates if they were calculated
    if (from && to) {
      onFromDateChange(from);
      onToDateChange(to);
    }
  };

  // Define all possible date range options with their permission keys
  // Note: Date calculations should be handled by parent component:
  // - "Last Month": Complete previous month (e.g., if current is Oct 2025, then Sep 1-30, 2025)
  // - "Last Year": Complete previous year (e.g., if current is 2025, then Jan 1 - Dec 31, 2024)
  // - "This Year": From Jan 1 of current year to current date
  const dateRangeOptions = [
    {
      value: "Today",
      label: "Today",
      permissionKey: "date_limit_Today",
    },
    {
      value: "Yesterday",
      label: "Yesterday",
      permissionKey: "date_limit_Yesterday",
    },
    {
      value: "Last 7 days",
      label: "Last 7 days",
      permissionKey: "date_limit_Last_7_days",
    },
    {
      value: "This Month",
      label: "This Month",
      permissionKey: "date_limit_This_Month",
    },
    {
      value: "Last Month", // Previous complete month only
      label: "Last Month",
      permissionKey: "date_limit_Last_Month",
    },
    {
      value: "Last 6 months",
      label: "Last 6 months",
      permissionKey: "date_limit_Last_6_Months",
    },
    {
      value: "This Year",
      label: "This Year",
      permissionKey: "date_limit_This_Year",
    },
    {
      value: "Last Year", // Previous complete year only
      label: "Last Year",
      permissionKey: "date_limit_Last_Year",
    },
    {
      value: "custom",
      label: "Custom",
      permissionKey: "date_limit_Custom",
    },
    {
      value: "limited custom",
      label: "Limited Custom",
      permissionKey: "date_limit_Limited_Custom",
    },
  ];

  const allowedDateRangeOptions = dateRangeOptions.filter(
    option => effectivePermissions[option.permissionKey] === true
  );

  const bloodDateRangeOptions = BLOOD_DATE_RANGE_VALUES.map(value =>
    dateRangeOptions.find(option => option.value === value)
  ).filter(Boolean) as typeof dateRangeOptions;

  const displayOptions =
    preset === "lab"
      ? dateRangeOptions.filter(option =>
          LAB_DATE_RANGE_VALUES.includes(option.value)
        )
      : preset === "blood"
        ? bloodDateRangeOptions
        : allowedDateRangeOptions.length > 0
          ? allowedDateRangeOptions
          : dateRangeOptions.filter(option => option.value === "Today");
  // Check if custom date selection is allowed
  const isCustomEnabled = dateRange === "custom" || dateRange === "limited custom";
  const isLimitedCustom = dateRange === "limited custom";

  // Calculate min and max dates for limited custom (1 month range)
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const minDate = oneMonthAgo.toISOString().split("T")[0];
  const maxDate = today.toISOString().split("T")[0];

  const showCustomDates = !compact || isCustomEnabled;

  return (
    <Box display="flex" gap={1.5} alignItems="flex-end" flexWrap="wrap">
      {/* Date Range Dropdown */}
      <Box sx={{ width: { xs: "100%", sm: compact ? "150px" : "120px" } }}>
        <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
          {preset === "lab" || preset === "blood" ? "Date Range" : "Date Limit"}
        </Typography>
        <Select
          size="small"
          value={dateRange}
          onChange={e => {
            const selectedRange = e.target.value as string;
            onDateRangeChange(selectedRange);
            calculateDates(selectedRange);
          }}
          className="custom-input"
          fullWidth
        >
          {displayOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* From Date */}
      {showCustomDates && <Box sx={{ width: { xs: "100%", md: "160px" } }}>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "500",
            mb: 0.5,
            color: isCustomEnabled ? "#000" : "#999",
          }}
        >
          From Date
        </Typography>
        <TextField
          className="custom-input"
          type="date"
          size="small"
          fullWidth
          value={fromDate}
          disabled={!isCustomEnabled}
          inputProps={{
            min: isLimitedCustom ? minDate : undefined,
            max: isLimitedCustom ? maxDate : undefined,
          }}
          onChange={e => {
            if (isCustomEnabled) {
              onFromDateChange(e.target.value);
              onDateRangeChange(dateRange);
            }
          }}
          sx={{
            background: isCustomEnabled ? "#F5F5FB" : "#f0f0f0",
            borderRadius: "12px",
            "& fieldset": { border: "none" },
            "& .MuiInputBase-input": {
              cursor: isCustomEnabled ? "text" : "not-allowed",
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>}

      {/* To Date */}
      {showCustomDates && <Box sx={{ width: { xs: "100%", md: "160px" } }}>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "500",
            mb: 0.5,
            color: isCustomEnabled ? "#000" : "#999",
          }}
        >
          To Date
        </Typography>
        <TextField
          className="custom-input"
          type="date"
          size="small"
          fullWidth
          value={toDate}
          disabled={!isCustomEnabled}
          inputProps={{
            min: isLimitedCustom ? minDate : undefined,
            max: isLimitedCustom ? maxDate : undefined,
          }}
          onChange={e => {
            if (isCustomEnabled) {
              onToDateChange(e.target.value);
              onDateRangeChange(dateRange);
            }
          }}
          sx={{
            background: isCustomEnabled ? "#F5F5FB" : "#f0f0f0",
            borderRadius: "12px",
            "& fieldset": { border: "none" },
            "& .MuiInputBase-input": {
              cursor: isCustomEnabled ? "text" : "not-allowed",
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
      </Box>}
    </Box>
  );
};

export default DateRangeSelector;
